import type { InputHTMLAttributes, ReactNode } from "react";
import { forwardRef } from "react";

type Props = Omit<InputHTMLAttributes<HTMLInputElement>, "className"> & {
  label?: string;
  hint?: string;
  error?: string;
  prefix?: ReactNode;
  suffix?: ReactNode;
  className?: string;
};

export const Input = forwardRef<HTMLInputElement, Props>(function Input(
  { label, hint, error, prefix, suffix, className = "", id, ...rest },
  ref,
) {
  const inputId = id ?? rest.name;
  return (
    <div className={`w-full ${className}`}>
      {label ? (
        <label
          htmlFor={inputId}
          className="block text-sm font-semibold text-navy-800"
        >
          {label}
          {rest.required ? (
            <span className="ml-1 text-accent-500" aria-hidden="true">
              *
            </span>
          ) : null}
        </label>
      ) : null}
      <div
        className={`mt-1.5 flex items-center gap-2 rounded-xl bg-white px-3.5 py-2.5 ring-1 transition-all focus-within:ring-2 ${
          error
            ? "ring-red-300 focus-within:ring-red-500"
            : "ring-navy-200 focus-within:ring-accent-500"
        }`}
      >
        {prefix ? <span className="text-navy-400">{prefix}</span> : null}
        <input
          id={inputId}
          ref={ref}
          className="w-full bg-transparent text-navy-800 placeholder:text-navy-400 focus:outline-none"
          aria-invalid={error ? "true" : undefined}
          aria-describedby={
            error ? `${inputId}-err` : hint ? `${inputId}-hint` : undefined
          }
          {...rest}
        />
        {suffix ? <span className="text-navy-400">{suffix}</span> : null}
      </div>
      {error ? (
        <p id={`${inputId}-err`} className="mt-1.5 text-xs text-red-600">
          {error}
        </p>
      ) : hint ? (
        <p id={`${inputId}-hint`} className="mt-1.5 text-xs text-navy-500">
          {hint}
        </p>
      ) : null}
    </div>
  );
});
